import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Button } from "flowbite-react";
import { useGetData } from "../../../../../hooks/useGetData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechaFormatosGeneralesInterface } from "../../../../../interfaces/FechaFormatosGeneralesInterface";
import { EnvioResultadosInterface } from "../../../../../interfaces/EnvioResultadosInterface";
import Header from "../../../../../components/HeaderForms/HeaderForms";
import { EnvioResultadosListaPrint } from "./EnvioResultadosListaPrint";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";

export const EnvioResultadosListaScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const tituloFormulario = "ENVÍO DE RESULTADOS";
  const [formData, setFormData] = useState({
    day: "",
    month: "",
    year: "",
  });

  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { control, handleSubmit, setValue, reset } = useForm();

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const queryFechaFG = useGetData<FechaFormatosGeneralesInterface>({
    url: `/fecha_formatosgenerales/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "fecha_formatosgenerales"],
  });

  const queryEnvioResultados = useGetData<EnvioResultadosInterface[]>({
    url: `/envio-resultados/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "envio-resultados"],
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const navigate = useNavigate();

  const handlePrint = () => {
    const printContent = document.getElementById("printableContent");

    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");

      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Vista Previa de Impresión</title>
              <style>
                @media print {
                  body {
                    margin: 20px;
                    font-family: Arial, sans-serif;
                  }
                }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `);

        const styles = document.styleSheets;
        for (let i = 0; i < styles.length; i++) {
          try {
            if (styles[i].cssRules) {
              const styleTag = printWindow.document.createElement("style");
              for (let j = 0; j < styles[i].cssRules.length; j++) {
                styleTag.appendChild(
                  printWindow.document.createTextNode(
                    styles[i].cssRules[j].cssText
                  )
                );
              }
              printWindow.document.head.appendChild(styleTag);
            }
          } catch (e) {
            console.warn("No se pudo copiar una hoja de estilo:", e);
          }
        }

        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 500);
        };
      } else {
        alert("No se pudo abrir la ventana de impresión.");
      }
    }
  };

  useEffect(() => {
    if (queryFechaFG.data) {
      const fechafg = queryFechaFG.data;
      reset({
        fecha_envio_res: fechafg.fecha_envio_res || "",
      });
    }
  }, [queryFechaFG.data, reset]);

  useEffect(() => {
    if (queryEnvioResultados.data && queryEnvioResultados.data.length > 0) {
      const ER = queryEnvioResultados.data[0];
      reset({
        folio_er: ER.folio_er || "",
        nombre_er: ER.nombre_er || "",
        cargo_er: ER.cargo_er || "",
        empresa_er: ER.empresa_er || "",
        descripcion_er1: ER.descripcion_er1 || "",
        descripcion_er2: ER.descripcion_er2 || "",
        descripcion_er3: ER.descripcion_er3 || "",
        descripcion_er4: ER.descripcion_er4 || "",
        evaluado_uuid: ER.evaluado_uuid || "",
      });
    }
  }, [queryEnvioResultados.data, reset]);

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`max-w-4xl mx-auto rounded-lg shadow-lg overflow-hidden mt-4 mb-4 relative ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
        {/* Header */}
        <Header titulo={tituloFormulario} />

        {/* Contenido del formulario */}
        <div className="p-6">
          <div className="text-left mb-8">
            <h2 className={`text-xl font-bold mb-2 flex items-center justify-end gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              <span>
                FOLIO:{" "}
                {queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                  ? queryEnvioResultados.data[0]?.folio_er || ""
                  : ""}
              </span>
            </h2>
            <h2 className={`text-xl font-bold mb-2 flex items-center justify-end gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              <span>
                FECHA:{" "}
                {queryFechaFG.data?.fecha_envio_res
                  ? (() => {
                      const date = new Date(queryFechaFG.data.fecha_envio_res);
                      date.setDate(date.getDate() + 1);
                      return date.toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      });
                    })()
                  : "Fecha no disponible"}
              </span>
            </h2>
            <h2 className={`text-xl font-bold mb-2 flex items-left ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              {`NOMBRE: ${
                queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                  ? queryEnvioResultados.data[0]?.nombre_er || ""
                  : ""
              }`}
            </h2>
            <h2 className={`text-xl font-bold mb-2 flex items-center gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              <span>
                CARGO:{" "}
                {queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                  ? queryEnvioResultados.data[0]?.cargo_er || ""
                  : ""}
              </span>
            </h2>
            <h2 className={`text-xl font-bold mb-2 flex items-center gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              <span>
                EMPRESA:{" "}
                {queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                  ? queryEnvioResultados.data[0]?.empresa_er || ""
                  : ""}
              </span>
            </h2>
            <h2 className={`text-xl font-bold mb-2 flex items-center gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              <span>PRESENTE</span>
            </h2>
          </div>

          <div className="space-y-6">
            <p className={`text-justify leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
              <span className="mr-2">
                {queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                  ? queryEnvioResultados.data[0]?.descripcion_er1 || ""
                  : ""}
              </span>
            </p>

            <p className={`text-justify ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
              No omito comentarle que resulta de suma importancia manejar los
              resultados con las debidas medidas de seguridad, para salvaguardar
              su confidencialidad y la de los evaluados.
            </p>

            <p className={`text-justify ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
              Cabe señalar que los resultados emitidos, emanan del apoyo que
              presta esta empresa en cumplimiento a la normatividad aplicable a la
              misma, ello en el respectivo ámbito de nuestras competencias y
              facultades, razón por la cual los resultados de las evaluaciones que
              solicita no serán vinculatorios a las determinaciones que emitan al
              respecto. Siendo estos únicamente de carácter informativo donde SKA
              TRACKING AND SECURITY S.A. de C.V. solo realiza conclusiones y
              recomendaciones a consideración y valoración estrictamente de la
              empresa contratante.
            </p>

            <p className={`text-justify ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
              En espera de que esta información contribuya a fortalecer el
              cumplimiento de las acciones encomendadas, me reitero a sus órdenes
              y aprovecho la ocasión para enviarle un cordial saludo.
            </p>
          </div>
          <div className={`flex flex-col items-center mt-4 w-full ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
            <span className="font-medium text-center">ATENTAMENTE</span>
            <span className="font-bold text-center">
              GONZALO FRANCISCO CARRILLO NAVARRETE
            </span>
            <span className="text-center">EN REPRESENTACIÓN LEGAL DE</span>
            <span className="text-center font-semibold">
              SKA TRACKING AND SECURITY S.A. DE C.V.
            </span>
          </div>
        </div>

        {/* Botones */}
        <div className="mt-4 mb-4 px-4">
          <div className="flex justify-between">
            <Button
              color="red"
              onClick={() => {
                window.scrollTo(0, 0);
                navigate(-1);
              }}
              className={`bg-red-500 hover:bg-red-600 text-white ${isDarkMode ? "bg-red-600 hover:bg-red-700" : ""}`}
            >
              Regresar
            </Button>
            <Button
              color="blue"
              className={`z-10 bg-blue-500 hover:bg-blue-600 text-white ${isDarkMode ? "bg-blue-600 hover:bg-blue-700" : ""}`}
              onClick={handlePrint}
            >
              Imprimir
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className={`h-12 mt-8 flex items-center justify-center ${isDarkMode ? "bg-blue-800" : "bg-customBlue"}`}>
          <p className="text-sm text-white px-4">
            <span className="font-bold">DOCUMENTO CONFIDENCIAL</span>, prohibido
            su reproducción, copia o sacar este documento de las Instalaciones de
            Skyangel
          </p>
        </div>

        {/* Imprimir */}
        <div id="printableContent" style={{ display: "none" }}>
          <EnvioResultadosListaPrint />
        </div>
      </div>
    </div>
  );
};
